import React, { useContext, useState } from 'react';
import { ShopContext } from '../context/ShopContext';
import { CloseIcon } from './Icons';
import './InfoModal.css';

const InfoModal = () => {
  const { infoModalState, closeInfoModal, setInfoModalKey, showToast } = useContext(ShopContext);

  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: 'General', message: '' });
  const [careerForm, setCareerForm] = useState({ name: '', email: '', role: 'Designer', portfolio: '' });

  if (!infoModalState || !infoModalState.open) return null;

  const currentTab = infoModalState.key || 'faq';

  const handleContactSubmit = (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      showToast('Please complete all contact form fields.', 'error');
      return;
    }
    showToast(`Thank you ${contactForm.name}! Message sent to techsupport@pallywear.com.`, 'success');
    setContactForm({ name: '', email: '', subject: 'General', message: '' });
  };

  const handleCareerSubmit = (e) => {
    e.preventDefault();
    if (!careerForm.name || !careerForm.email) {
      showToast('Please fill out your name and email address.', 'error');
      return;
    }
    showToast(`Application received for ${careerForm.role}! We will reach out to ${careerForm.email}.`, 'success');
    setCareerForm({ name: '', email: '', role: 'Designer', portfolio: '' });
  };

  return (
    <div className="info-modal-overlay animate-fade-in" onClick={closeInfoModal}>
      <div className="info-modal-container glass animate-slide-in" onClick={(e) => e.stopPropagation()}>
        
        {/* Header & Navigation Bar */}
        <div className="info-modal-header">
          <div className="info-modal-title-wrap">
            <h3 className="info-modal-title">
              {currentTab === 'faq' && '❓ Frequently Asked Questions'}
              {currentTab === 'shipping' && '🚚 Shipping & Return Policy'}
              {currentTab === 'sizeguide' && '📏 Official Size & Measurement Guide'}
              {currentTab === 'contact' && '💬 Contact Support & Customer Care'}
              {currentTab === 'privacy' && '🔒 Official Privacy Policy'}
              {currentTab === 'terms' && '⚖️ Terms of Service & Conditions'}
              {currentTab === 'story' && '✨ Our Brand Story'}
              {currentTab === 'sustainability' && '🌱 Sustainability & Eco Ethics'}
              {currentTab === 'careers' && '🚀 Careers at PallyWear'}
              {currentTab === 'press' && '📰 Press & Media Features'}
            </h3>
            <span className="info-badge">PallyWear Help Center</span>
          </div>

          <button type="button" className="info-close-btn" onClick={closeInfoModal} title="Close">
            <CloseIcon size={20} />
          </button>
        </div>

        {/* Tab Selector Pills */}
        <div className="info-tabs-bar">
          <button 
            type="button" 
            className={`info-tab-pill ${currentTab === 'faq' ? 'active' : ''}`}
            onClick={() => setInfoModalKey('faq')}
          >
            FAQ
          </button>
          <button 
            type="button" 
            className={`info-tab-pill ${currentTab === 'shipping' ? 'active' : ''}`}
            onClick={() => setInfoModalKey('shipping')}
          >
            Shipping & Returns
          </button>
          <button 
            type="button" 
            className={`info-tab-pill ${currentTab === 'sizeguide' ? 'active' : ''}`}
            onClick={() => setInfoModalKey('sizeguide')}
          >
            Size Guide
          </button>
          <button 
            type="button" 
            className={`info-tab-pill ${currentTab === 'contact' ? 'active' : ''}`}
            onClick={() => setInfoModalKey('contact')}
          >
            Contact Us
          </button>
          <button 
            type="button" 
            className={`info-tab-pill ${currentTab === 'privacy' ? 'active' : ''}`}
            onClick={() => setInfoModalKey('privacy')}
          >
            Privacy Policy
          </button>
          <button 
            type="button" 
            className={`info-tab-pill ${currentTab === 'terms' ? 'active' : ''}`}
            onClick={() => setInfoModalKey('terms')}
          >
            Terms of Service
          </button>
          <button 
            type="button" 
            className={`info-tab-pill ${currentTab === 'story' ? 'active' : ''}`}
            onClick={() => setInfoModalKey('story')}
          >
            Our Story
          </button>
          <button 
            type="button" 
            className={`info-tab-pill ${currentTab === 'sustainability' ? 'active' : ''}`}
            onClick={() => setInfoModalKey('sustainability')}
          >
            Sustainability
          </button>
          <button 
            type="button" 
            className={`info-tab-pill ${currentTab === 'careers' ? 'active' : ''}`}
            onClick={() => setInfoModalKey('careers')}
          >
            Careers
          </button>
          <button 
            type="button" 
            className={`info-tab-pill ${currentTab === 'press' ? 'active' : ''}`}
            onClick={() => setInfoModalKey('press')}
          >
            Press
          </button>
        </div>

        {/* Body Content Sections */}
        <div className="info-modal-body custom-scrollbar">
          
          {/* FAQ Tab Content */}
          {currentTab === 'faq' && (
            <div className="info-content-section animate-fade-in">
              <div className="faq-list">
                <div className="faq-card glass">
                  <h4>Q: How long will standard delivery take?</h4>
                  <p>Standard delivery across Metro cities takes 2-4 business days. Regional and rest-of-India orders deliver within 4-6 business days. Express shipping options are available at checkout.</p>
                </div>

                <div className="faq-card glass">
                  <h4>Q: What materials are used in PallyWear streetwear?</h4>
                  <p>All PallyWear garments are crafted using 240+ GSM 100% combed organic cotton with pre-shrunk bio-wash finishing for maximum softness and structural drape.</p>
                </div>

                <div className="faq-card glass">
                  <h4>Q: How can I track my package?</h4>
                  <p>Once your order is processed, a unique <code>ORD-XXXXXX</code> reference ID is generated. You can click <strong>Track Order</strong> in the top menu anytime to view real-time courier updates.</p>
                </div>

                <div className="faq-card glass">
                  <h4>Q: What is your 15-day exchange policy?</h4>
                  <p>We offer hassle-free 15-day returns and size exchanges. Items must be unworn with original tags attached. Return courier pickups are arranged directly from your doorstep.</p>
                </div>

                <div className="faq-card glass">
                  <h4>Q: Are payment gateways safe and encrypted?</h4>
                  <p>Yes. All transactions are processed through 256-bit SSL encrypted PCI-DSS compliant gateways supporting Google Pay, PhonePe, Paytm, Cards, and Net Banking.</p>
                </div>
              </div>
            </div>
          )}

          {/* Shipping & Returns Content */}
          {currentTab === 'shipping' && (
            <div className="info-content-section animate-fade-in">
              <div className="shipping-grid">
                <div className="info-feature-box glass">
                  <span className="feature-icon">🚚</span>
                  <h4>Free Pan-India Shipping</h4>
                  <p>Enjoy free express delivery on all orders over ₹999. Standard shipping is ₹99 flat rate for orders below ₹999.</p>
                </div>

                <div className="info-feature-box glass">
                  <span className="feature-icon">🔄</span>
                  <h4>15-Day Easy Returns</h4>
                  <p>If you need a different size or fit, initiate a doorstep exchange within 15 days of receiving your package.</p>
                </div>

                <div className="info-feature-box glass">
                  <span className="feature-icon">📦</span>
                  <h4>Doorstep Pickup Service</h4>
                  <p>Our courier partner will arrive at your address within 24-48 hours to inspect and collect the return parcel.</p>
                </div>

                <div className="info-feature-box glass">
                  <span className="feature-icon">⚡</span>
                  <h4>Instant Refunds</h4>
                  <p>Refunds are initiated back to your original payment method (UPI / Bank Account) within 24 hours of package quality check.</p>
                </div>
              </div>
            </div>
          )}

          {/* Size Guide Content */}
          {currentTab === 'sizeguide' && (
            <div className="info-content-section animate-fade-in">
              <p className="size-intro-text">
                PallyWear garments feature a modern <strong>Oversized Streetwear Silhouette</strong>. If you prefer a standard slim fit, we recommend ordering one size down.
              </p>

              <div className="table-responsive">
                <table className="size-chart-table">
                  <thead>
                    <tr>
                      <th>Size Label</th>
                      <th>Chest (Inches)</th>
                      <th>Length (Inches)</th>
                      <th>Shoulder (Inches)</th>
                      <th>Recommended Fit</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>S (Small)</strong></td>
                      <td>38 - 40"</td>
                      <td>28"</td>
                      <td>19.5"</td>
                      <td>Slim / Standard</td>
                    </tr>
                    <tr>
                      <td><strong>M (Medium)</strong></td>
                      <td>41 - 43"</td>
                      <td>29"</td>
                      <td>20.5"</td>
                      <td>Relaxed Streetwear</td>
                    </tr>
                    <tr>
                      <td><strong>L (Large)</strong></td>
                      <td>44 - 46"</td>
                      <td>30"</td>
                      <td>21.5"</td>
                      <td>Classic Oversized</td>
                    </tr>
                    <tr>
                      <td><strong>XL (X-Large)</strong></td>
                      <td>47 - 49"</td>
                      <td>31"</td>
                      <td>22.5"</td>
                      <td>Drop Shoulder Boxy</td>
                    </tr>
                    <tr>
                      <td><strong>XXL (2X-Large)</strong></td>
                      <td>50 - 52"</td>
                      <td>32"</td>
                      <td>23.5"</td>
                      <td>Super Heavyweight Oversized</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Contact Us Content */}
          {currentTab === 'contact' && (
            <div className="info-content-section animate-fade-in">
              <div className="contact-layout-grid">
                <div className="contact-details-box glass">
                  <h4>📞 Contact Info</h4>
                  <p>Our support specialists are online 6 days a week to help with order tracking, sizing, and style consultations.</p>
                  
                  <div className="contact-info-list">
                    <div className="contact-info-row">
                      <span>📧 Customer Support Email:</span>
                      <strong>techsupport@pallywear.com</strong>
                    </div>
                    <div className="contact-info-row">
                      <span>📱 WhatsApp Line:</span>
                      <strong>+91 95975 28585</strong>
                    </div>
                    <div className="contact-info-row">
                      <span>🕒 Support Hours:</span>
                      <strong>10:00 AM - 8:00 PM IST (Mon-Sat)</strong>
                    </div>
                    <div className="contact-info-row">
                      <span>📍 Headquarters:</span>
                      <strong>PallyWear Studios, No. 4, 244, Muthamil Nagar, Old Perungalathur, Chennai, Tamil Nadu 600063</strong>
                    </div>
                  </div>
                </div>

                <div className="contact-form-box glass">
                  <h4>✉️ Send Us a Direct Message</h4>
                  <form onSubmit={handleContactSubmit} className="contact-inquiry-form">
                    <div className="form-group">
                      <label>Your Full Name</label>
                      <input 
                        type="text" 
                        placeholder="Alex Rivera"
                        value={contactForm.name}
                        onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                        className="input-field"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Email Address</label>
                      <input 
                        type="email" 
                        placeholder="alex@example.com"
                        value={contactForm.email}
                        onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                        className="input-field"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Subject</label>
                      <select 
                        value={contactForm.subject}
                        onChange={(e) => setContactForm(prev => ({ ...prev, subject: e.target.value }))}
                        className="input-field"
                      >
                        <option value="General">General Question</option>
                        <option value="Order Status">Order Status Inquiry</option>
                        <option value="Exchange">Return / Size Exchange</option>
                        <option value="Collab">Brand Collaboration</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Message</label>
                      <textarea 
                        rows="3"
                        placeholder="How can we help you today?"
                        value={contactForm.message}
                        onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                        className="input-field"
                        required
                      />
                    </div>

                    <button type="submit" className="btn btn-primary send-msg-btn">
                      Submit Message →
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Our Story Content */}
          {currentTab === 'story' && (
            <div className="info-content-section animate-fade-in">
              <div className="story-hero-block glass">
                <h3>Crafted for Longevity, Designed for the Future</h3>
                <p>
                  PallyWear was founded in 2024 with a clear mandate: bridge the gap between luxury haute couture finishes and accessible everyday streetwear.
                </p>
                <p>
                  We engineer garments with heavyweight 240+ GSM combed cotton, reinforced flatlock stitching, and custom drop-shoulder cuts. Every thread is selected to maintain shape, color density, and comfort through hundreds of washes.
                </p>
              </div>
            </div>
          )}

          {/* Sustainability Content */}
          {currentTab === 'sustainability' && (
            <div className="info-content-section animate-fade-in">
              <div className="sustainability-list">
                <div className="eco-card glass">
                  <span className="eco-icon">🌿</span>
                  <div>
                    <h4>100% GOTS Certified Cotton</h4>
                    <p>All fabrics are woven from Global Organic Textile Standard certified organic cotton, grown without toxic synthetic pesticides or fertilizers.</p>
                  </div>
                </div>

                <div className="eco-card glass">
                  <span className="eco-icon">♻️</span>
                  <div>
                    <h4>Zero-Plastic Packaging</h4>
                    <p>Orders are shipped in 100% home-compostable mailer bags made from plant starch and non-toxic water-based inks.</p>
                  </div>
                </div>

                <div className="eco-card glass">
                  <span className="eco-icon">☀️</span>
                  <div>
                    <h4>Ethical Micro-Factories</h4>
                    <p>We partner exclusively with WRAP-certified facilities powered by solar energy, guaranteeing fair living wages and safe working environments.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Careers Content */}
          {currentTab === 'careers' && (
            <div className="info-content-section animate-fade-in">
              <div className="careers-header-box">
                <h4>Join the PallyWear Collective</h4>
                <p>We are expanding our design, e-commerce, and creative production teams. Apply for open positions below:</p>
              </div>

              <div className="careers-openings-grid">
                <div className="career-card glass">
                  <h5>Senior Streetwear Apparel Designer</h5>
                  <p>Chennai Studio • Full-Time</p>
                  <small>Lead tech pack development, oversized silhouette grading, and graphic artwork prints.</small>
                </div>

                <div className="career-card glass">
                  <h5>E-Commerce Growth & Performance Marketer</h5>
                  <p>Remote / Hybrid • Full-Time</p>
                  <small>Manage Meta/Google ad spend, retention funnels, and customer acquisition channels.</small>
                </div>
              </div>

              <form onSubmit={handleCareerSubmit} className="career-apply-form glass">
                <h5>Quick Job Application</h5>
                <div className="form-row-grid">
                  <input 
                    type="text" 
                    placeholder="Full Name"
                    value={careerForm.name}
                    onChange={(e) => setCareerForm(prev => ({ ...prev, name: e.target.value }))}
                    className="input-field"
                    required
                  />
                  <input 
                    type="email" 
                    placeholder="Email Address"
                    value={careerForm.email}
                    onChange={(e) => setCareerForm(prev => ({ ...prev, email: e.target.value }))}
                    className="input-field"
                    required
                  />
                </div>
                <input 
                  type="text" 
                  placeholder="Portfolio / LinkedIn Link"
                  value={careerForm.portfolio}
                  onChange={(e) => setCareerForm(prev => ({ ...prev, portfolio: e.target.value }))}
                  className="input-field"
                  style={{ marginTop: '0.75rem' }}
                />
                <button type="submit" className="btn btn-primary" style={{ marginTop: '0.75rem' }}>
                  Submit Resume →
                </button>
              </form>
            </div>
          )}

          {/* Press Content */}
          {currentTab === 'press' && (
            <div className="info-content-section animate-fade-in">
              <div className="press-mentions-grid">
                <div className="press-card glass">
                  <span className="press-outlet">GQ INDIA</span>
                  <p className="press-quote">"PallyWear is redefining drop-shoulder silhouettes with heavyweight cotton and minimal modern branding."</p>
                </div>

                <div className="press-card glass">
                  <span className="press-outlet">VOGUE STREETWEAR</span>
                  <p className="press-quote">"One of the fastest growing independent labels prioritizing GOTS organic cotton and zero-plastic packaging."</p>
                </div>

                <div className="press-card glass">
                  <span className="press-outlet">HYPEBEAST</span>
                  <p className="press-quote">"The perfect blank canvas — high density weave, durable collars, and custom tailored drape."</p>
                </div>
              </div>

              <div className="press-kit-box glass" style={{ marginTop: '1.25rem', padding: '1rem', textAlign: 'center' }}>
                <p style={{ margin: 0, fontSize: '0.9rem' }}>
                  For press kits, high-res lookbook assets, or editorial inquiries, reach out to <strong>press@pallywear.com</strong>
                </p>
              </div>
            </div>
          )}

          {/* Privacy Policy Content */}
          {currentTab === 'privacy' && (
            <div className="info-content-section animate-fade-in">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
                <div className="eco-card glass" style={{ border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                  <span className="eco-icon">🔒</span>
                  <div>
                    <h4 style={{ color: '#10b981' }}>256-Bit SSL Encryption</h4>
                    <p style={{ fontSize: '0.85rem' }}>End-to-end cryptographic protection for all browsing and checkout activity.</p>
                  </div>
                </div>

                <div className="eco-card glass" style={{ border: '1px solid rgba(0, 112, 243, 0.3)' }}>
                  <span className="eco-icon">🛡️</span>
                  <div>
                    <h4 style={{ color: '#60a5fa' }}>PCI-DSS Level 1 Safe</h4>
                    <p style={{ fontSize: '0.85rem' }}>Direct Razorpay gateway integration. We never store card numbers.</p>
                  </div>
                </div>
              </div>

              <div className="faq-list">
                <div className="faq-card glass">
                  <h4>1. What Information We Collect</h4>
                  <p>When you place an order on PallyWear, we collect your name, shipping address, contact phone number, and email address. This data is strictly utilized to process your order, dispatch tracking alerts, and ensure reliable doorstep courier delivery.</p>
                </div>

                <div className="faq-card glass">
                  <h4>2. Payment Security & Bank SMS OTPs</h4>
                  <p>All online payments (UPI, Google Pay, PhonePe, Paytm, Visa, MasterCard, RuPay, Net Banking) are securely routed through Razorpay. Sensitive card and bank details are authenticated directly with your issuing bank via 3D Secure SMS OTPs.</p>
                </div>

                <div className="faq-card glass">
                  <h4>3. Courier Logistics & Third Parties</h4>
                  <p>We share necessary shipping details (recipient name, address, and mobile number) exclusively with our verified courier partners (such as Delhivery, BlueDart, IndiaPost) solely for package fulfillment.</p>
                </div>

                <div className="faq-card glass">
                  <h4>4. Data Rights & Inquiries</h4>
                  <p>You have full rights to request an export or complete deletion of your customer account and order records. For privacy inquiries, please contact our data team at <strong>privacy@pallywear.com</strong>.</p>
                </div>
              </div>
            </div>
          )}

          {/* Terms of Service Content */}
          {currentTab === 'terms' && (
            <div className="info-content-section animate-fade-in">
              <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1rem 1.25rem', borderRadius: '12px', border: '1px solid var(--border)', marginBottom: '1.25rem' }}>
                <h4 style={{ margin: '0 0 0.4rem 0', fontWeight: '700' }}>Official Merchant Entity</h4>
                <p style={{ margin: 0, fontSize: '0.88rem', opacity: 0.9, lineHeight: '1.5' }}>
                  <strong>PALLYWEAR GIFTING SOLUTIONS PRIVATE LIMITED</strong><br />
                  Kandigai, Chennai, Tamil Nadu, India • GST & MSME Registered
                </p>
              </div>

              <div className="faq-list">
                <div className="faq-card glass">
                  <h4>1. Acceptance of Terms</h4>
                  <p>By accessing or placing an order through <strong>pallywear.com</strong>, you agree to be bound by these Terms of Service, all applicable laws and regulations in India.</p>
                </div>

                <div className="faq-card glass">
                  <h4>2. Pricing & Product Accuracy</h4>
                  <p>All product prices are quoted in Indian National Rupees (INR) and are inclusive of applicable GST taxes. While we strive to display colors and textures accurately, minor monitor variations may occur.</p>
                </div>

                <div className="faq-card glass">
                  <h4>3. Orders & Payment Settlement</h4>
                  <p>Orders are confirmed upon successful authorization by your bank or UPI provider. PallyWear reserves the right to cancel or decline any order in cases of suspected fraudulent activity or pricing errors, with full immediate refund.</p>
                </div>

                <div className="faq-card glass">
                  <h4>4. 15-Day Exchange Policy</h4>
                  <p>Unworn, unwashed garments with original tags attached can be exchanged or returned within 15 days of delivery. Doorstep return pickups are scheduled upon request.</p>
                </div>

                <div className="faq-card glass">
                  <h4>5. Intellectual Property</h4>
                  <p>All trademarks, graphics, logos, product photographs, and streetwear designs displayed on this website are the exclusive intellectual property of PallyWear Gifting Solutions Pvt Ltd.</p>
                </div>

                <div className="faq-card glass">
                  <h4>6. Governing Law & Jurisdiction</h4>
                  <p>These terms shall be governed by and construed in accordance with the laws of India. Any disputes arising out of transactions on this platform shall be subject to the exclusive jurisdiction of the courts in Chennai, Tamil Nadu.</p>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default InfoModal;
